import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { colors } from '@innovationdepartment/proxima-ui';
import { Link } from 'react-router';

/* Status | Workflow type | Id | Started | Duration | Trigger | Schedule | Actions | Expand

   Every data track is fixed so the fields pack against the left edge; the slack lives in the
   actions track, whose cell justifies to flex-end, anchoring the buttons to the right.

   Widths are measured against what actually renders, so nothing truncates:
   - 272px seats the longest catalogued name, "Klaviyo Transform Load Reprocess" (269px at
     body1Semibold's 16px/600). Longer uncatalogued raw types still ellipsise — the cell's
     min-width: 0 lets Text's ellipsis engage, so an overlong name is clipped at the track edge
     with its tooltip intact rather than spilling into the id.
   - 148px seats the widest timestamp, "May 28, 2026 12:48AM" (145px at body2's 14px/400). The
     wide cases are a 2-digit hour paired with May's wide glyphs.

   Dropping Ended is what pays for both: Duration already carries the run's shape, and the exact
   end time is one expand away in the tasks panel. The row stops shrinking at the sum below —
   fixed tracks 976 + gaps 128 + padding 48 — and past that the list scrolls sideways rather than
   compressing columns into each other, so every field keeps its own track on one line. */
const EXECUTION_ROW_MIN_WIDTH = 1152;

export const executionRowGrid = css`
  display: grid;
  grid-template-columns: 100px 272px 60px 148px 76px 96px 124px minmax(72px, 1fr) 28px;
  gap: 16px;
  align-items: center;
  min-width: ${EXECUTION_ROW_MIN_WIDTH}px;
`;

/* Retry and Run history are the only actions, so they sit as matching 32px icon boxes */
const iconActionBox = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${colors.neutral30};
  border-radius: 4px;
  background: transparent;
  color: ${colors.neutral80};
  cursor: pointer;

  svg {
    font-size: 18px;
  }
`;

// Page Header — tabs left, "Updated" stamp right, inside SectionLayout's headerContent
export const HeaderRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
`;

/* Snap the tab indicator like Creative Engine does — the slide janks on mount */
export const TabsWrapper = styled.div`
  .MuiTabs-indicator {
    transition: none;
  }
`;

// Status List
export const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  /* Rows hold their min width, so a narrow viewport scrolls the whole list — header included —
     rather than reflowing the columns */
  overflow-x: auto;
`;

export const Card = styled.div<{ failed: boolean }>`
  background: ${colors.white};
  border: 1px solid ${colors.neutral20};
  border-left: ${({ failed }) =>
    failed ? `3px solid ${colors.red60}` : `1px solid ${colors.neutral20}`};
  border-radius: 8px;
  overflow: hidden;
  /* Grow to the row inside instead of clipping it once the row hits its min width */
  min-width: fit-content;
`;

export const HeaderLabels = styled.div`
  ${executionRowGrid};
  padding: 0 24px;

  span {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${colors.neutral60};
  }
`;

export const RowMain = styled.div`
  ${executionRowGrid};
  padding: 20px 24px;
  cursor: pointer;

  &:hover {
    background: ${colors.neutral0};
  }
`;

export const ExpandIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${colors.neutral60};
  padding: 4px;
`;

/* min-width: 0 defeats a grid item's automatic min-content floor — without it the name
   refuses to shrink and spills into the next column instead of ellipsising */
export const WorkflowTypeCell = styled.div`
  min-width: 0;
`;

/* Copyable row fields sit in these so clicks select text instead of toggling the row */
export const NonToggleText = styled.span`
  display: inline-block;
  justify-self: start;
  width: fit-content;
  max-width: 100%;
  cursor: text;
  user-select: text;
`;

/* Click-to-copy chip showing the shortened id; hover surfaces the copy affordance.
   The negative margin cancels the chip's own left padding so the glyphs line up with the ID
   header above them — the hover background keeps its breathing room, and bleeds only into the
   column gap. */
export const ExecutionId = styled.span`
  display: inline-flex;
  align-items: center;
  justify-self: start;
  width: fit-content;
  max-width: 100%;
  padding: 2px 8px;
  margin-left: -8px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 13px;
  color: ${colors.neutral80};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: ${colors.neutral5};
    color: ${colors.neutral100};
  }
`;

/* Navigation must stay an anchor (middle-click, open-in-new-tab), so this is a Link
   dressed as an icon button to sit beside the Retry button */
export const RunHistoryLink = styled(Link)`
  ${iconActionBox};
  text-decoration: none;

  &:hover {
    background: ${colors.neutral5};
    color: ${colors.neutral100};
  }
`;

/* Sized to match RunHistoryLink beside it — same box, just in red */
export const RetryButton = styled.button`
  ${iconActionBox};
  border-color: ${colors.red60};
  color: ${colors.red60};

  &:hover:not(:disabled) {
    background: ${colors.red5};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ActionsCell = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export const Cron = styled.code`
  font-family: monospace;
  background: ${colors.neutral5};
  border: 1px solid ${colors.neutral20};
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 13px;
  color: ${colors.neutral80};
  white-space: nowrap;
`;

// Manual Triggers
export const TriggerRowMain = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 180px 28px;
  gap: 24px;
  align-items: center;
  padding: 20px 24px;
  cursor: pointer;

  &:hover {
    background: ${colors.neutral0};
  }
`;

export const WorkflowDescription = styled.div`
  margin-top: 2px;
`;

export const LastRunLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${colors.neutral60};
  margin-bottom: 4px;
`;

export const PayloadPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 24px 20px;
`;

export const PayloadHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
`;

export const PayloadTextarea = styled.textarea`
  font-family: monospace;
  font-size: 13px;
  color: ${colors.neutral90};
  background: ${colors.white};
  border: 1px solid ${colors.neutral20};
  border-radius: 4px;
  padding: 12px;
  min-height: 120px;
  width: 100%;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${colors.neutral60};
  }
`;

export const PayloadLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.neutral60};
`;

export const PayloadError = styled.div`
  font-size: 13px;
  color: ${colors.red60};
`;

export const PayloadActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

// Retry Confirmation Popup
export const ConfirmContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 320px;
`;

export const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;
